import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mic, Loader2, ChevronDown, ChevronUp, ArrowRight, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ACCENT_CLASSES = {
  indigo: { icon: "text-indigo-400", btn: "bg-primary hover:bg-primary/90", ring: "focus:ring-indigo-400", border: "border-indigo-400/20" },
  teal: { icon: "text-teal-400", btn: "bg-teal-500 hover:bg-teal-400", ring: "focus:ring-teal-400", border: "border-teal-400/20" },
  fuchsia: { icon: "text-fuchsia-400", btn: "bg-fuchsia-500 hover:bg-fuchsia-400", ring: "focus:ring-fuchsia-400", border: "border-fuchsia-400/20" },
  amber: { icon: "text-amber-400", btn: "bg-amber-500 hover:bg-amber-400", ring: "focus:ring-amber-400", border: "border-amber-400/20" },
  emerald: { icon: "text-emerald-400", btn: "bg-emerald-500 hover:bg-emerald-400", ring: "focus:ring-emerald-400", border: "border-emerald-400/20" },
};

export default function InterviewPrepCard({ track, day, accent = "indigo" }) {
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const c = ACCENT_CLASSES[accent] || ACCENT_CLASSES.indigo;
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/interview-prep/${track}/${day}`);
      setQuestions(data.questions);
      setOpenIndex(0);
    } catch (e) {
      toast.error("Couldn't load interview prep — try again");
    } finally {
      setLoading(false);
    }
  };

  const practiceWithResume = async () => {
    setResumeLoading(true);
    try {
      const { data } = await api.get("/resume-builder/interview-context");
      if (!data.resume_context) {
        toast.error("Add a Target Role in the Resume Builder's Basics step first, then save your draft");
        return;
      }
      navigate("/interview", { state: { resumeContext: data.resume_context, targetRole: data.target_role } });
    } catch {
      toast.error("Could not load your resume context right now");
    } finally {
      setResumeLoading(false);
    }
  };

  return (
    <div className={`glass-panel rounded-2xl p-6 border ${c.border}`} data-testid="interview-prep-card">
      <h3 className="font-heading font-semibold text-white mb-2 flex items-center gap-2">
        <Mic className={`w-4 h-4 ${c.icon}`} /> Interview Prep for This Topic
      </h3>
      <p className="text-sm text-slate-400 mb-4">
        See how this exact topic gets asked about in real interviews, with model answers.
      </p>

      {!questions ? (
        <button
          onClick={load}
          disabled={loading}
          data-testid="interview-prep-generate-btn"
          className={`flex items-center gap-2 ${c.btn} disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
          {loading ? "Generating…" : "Show Interview Questions"}
        </button>
      ) : (
        <div className="space-y-2.5" data-testid="interview-prep-results">
          {questions.map((q, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={i} className="border border-white/10 rounded-xl overflow-hidden" data-testid={`interview-prep-item-${i}`}>
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  data-testid={`interview-prep-question-${i}`}
                  className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <span className="text-sm text-white font-medium">{q.question}</span>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="px-4 py-3 text-sm text-slate-300 leading-relaxed" data-testid={`interview-prep-answer-${i}`}>
                    {q.answer}
                  </div>
                )}
              </div>
            );
          })}
          <Link
            to={`/interview?track=${track}`}
            data-testid="interview-prep-mock-interview-link"
            className={`mt-1 flex items-center justify-center gap-2 ${c.btn} text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors`}
          >
            Practice in Mock Interview <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      <button
        onClick={practiceWithResume} disabled={resumeLoading} data-testid="interview-prep-resume-based-btn"
        className="mt-3 w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 disabled:opacity-60 border border-white/10 text-slate-300 hover:text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
      >
        {resumeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Practice a Resume-Based Interview Instead
      </button>
    </div>
  );
}
